<script setup>

import MaskIcon from "@/Components/MaskIcon.vue";
import Badge from "@/Components/Badge.vue";
import MessageString from "@/Components/Messages/MessageString.vue";
import MessageSystem from "@/Components/Messages/MessageSystem.vue";
import MessageDate from "@/Components/Messages/MessageDate.vue";
import dayjs from "dayjs";
import Input from "@/Components/Input.vue";
import {computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch} from "vue";
import Modal from "@/Components/Modal.vue";
import AkLogo from "@/Components/Icons/AkLogo.vue";
import {Link, router, useForm, usePage} from "@inertiajs/vue3";
import {useThread} from "@/Store/Thread.js";
import {storeToRefs} from "pinia";
import Loading from "@/Components/Loading.vue";
import Spinner from "@/Components/Icons/Spinner.vue";
import {route} from "ziggy-js";
import debounce from "lodash-es/debounce.js";
import Button from "@/Components/Button.vue";
import {reachYaGoal, setMessages} from "@/helper.js";
import {useUser} from "@/Store/User.js";
import StarReview from "@/Components/Icons/StarReview.vue";

const props = defineProps({
    contentClasses: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false,
    },
    viewOffer: {
        type: Object,
        default: null
    }
})


const showModal = ref(false),
    dataImg = ref(null),
    files = ref([]),
    upload = ref(null),
    page = usePage(),
    {thread, messages, dialogElement, inputs, selectedUser} = storeToRefs(useThread()),
    {getUserStatusByIdOrStatus} = storeToRefs(useUser()),
    currentPageMessage = ref(1),
    isLoading = ref(false),
    offer = ref(thread.value?.user?.offer),
    oldHeight = ref(null),
    needAuth = ref(false),
    inputMsg = ref(null)

const sortedMessages = computed(() => {
    return messages.value ? Object.values(messages.value).sort((a, b) => {
        const aId = typeof a.id === 'string' && a.id.startsWith('opt_') ? Number.MAX_SAFE_INTEGER : a.id
        const bId = typeof b.id === 'string' && b.id.startsWith('opt_') ? Number.MAX_SAFE_INTEGER : b.id
        // Fallback for multiple optimistic messages to sort by time
        if (aId === Number.MAX_SAFE_INTEGER && bId === Number.MAX_SAFE_INTEGER) {
            return new Date(a.created_at) - new Date(b.created_at);
        }
        return aId - bId
    }) : []
})

const uploadedFile = ref({});

const formMessage = useForm({
    message: thread.value ?
        (inputs.value[thread.value.id] ? inputs.value[thread.value.id] : '') :
        (inputs.value['untitled'] ? inputs.value['untitled'] : ''),
    user_id: undefined,
})
const formFile = useForm({
    user_id: undefined,
    file: undefined
})
const fetch = (nextPage = false) => {
    if (!nextPage) {
        currentPageMessage.value = 1
    }
    isLoading.value = true

    const isThreadAdmin = route().current('admin.threads.*');
    const routeName = isThreadAdmin ? 'admin.threads.show' : 'threads.show';
    const componentName = isThreadAdmin ? 'Admin/Thread/Show' : 'Thread/Index';

    axios.get(route(routeName, {id: thread.value.id}) + `?message_page=${currentPageMessage.value}`, {
        headers: {
            "Content-type": 'application/json',
            "X-Inertia": true,
            "X-Inertia-Partial-Component": componentName,
            "X-Inertia-Partial-Data": 'messages',
            "X-Inertia-Version": page.version
        }
    }).then((resp) => {
        setMessages([...resp.data.props.messages.data, ...Object.values(messages.value)])
        nextTick().then(() => {
            dialogElement.value.scrollBy(0, dialogElement.value.scrollHeight - oldHeight.value)
            oldHeight.value = dialogElement.value.scrollHeight
        })
    }).finally(() => {
        isLoading.value = false
    })
}
const fetchDebounced = debounce(fetch, 1000)

const nextPageFunc = () => {
    if (isLoading.value) return;
    if (currentPageMessage.value < page.props.messages.last_page) {
        currentPageMessage.value++
        fetch(true)
    }
}

const haveChat = computed(() => {
    if (page.props.threads) {
        return page.props.threads.data.length > 0;
    }
    return false;
})

function inputFilter(newFile, oldFile, prevent) {
    if (!checkAuth()) return false;
    if (newFile && !oldFile) {
        if (newFile.type === 'application/pdf' || newFile.type.substr(0, 6) === 'image/') {
            uploadedFile.value.file = newFile.file;
            if(newFile.type.substr(0, 6) === 'image/') {
                uploadedFile.value.url = URL.createObjectURL(newFile.file);
            }

            upload.value.active = true
        }
        // Before adding a file
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
            return prevent()
        }

        // Filter php html js file
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
            return prevent()
        }
    }
}

const dateComparison = (pre, current) => {

    if (dayjs(pre).isValid() && typeof pre !== 'undefined') {
        return dayjs(pre).format('YYYY-MM-DD') !== dayjs(current).format('YYYY-MM-DD')
    } else {
        return true;
    }
}

const createChat = (userId) => {
    if(!inputs.value['untitled']) return;
    router.post(route('admin.messages.store'), {
            user_id: userId,
            message: inputs.value['untitled'],
        },
        {
            preserveState: true,
            preserveScroll: true,
            only: ['thread', 'messages'],
            onFinish: () => {
                thread.value = page.props.thread;
                setMessages(page.props.messages.data);
            }
        })
}

const sendMsg = () => {
    if (!checkAuth()) return;
    reachYaGoal('send-chat-message')
    if(!thread.value && !selectedUser.value?.id) return;
    if(!thread.value && selectedUser.value?.id) {
        createChat(selectedUser.value?.id);
        inputs.value['untitled'] = '';
        formMessage.reset()
    } else {
        // Fallback: If we are viewing an offer, and thread has no user (or thread is just a placeholder), use offer.user
        let targetId = thread.value?.user?.id;
        if (!targetId && props.viewOffer?.user?.id) {
            targetId = props.viewOffer.user.id;
        }
        
        formMessage.user_id = targetId;

        if (formMessage.message) {
            const msgContent = formMessage.message;
            const msgUserId = formMessage.user_id;

            const tempId = 'opt_' + Date.now();
            
            // Optimistic update
            const optimisticMsg = {
                id: tempId,
                body: msgContent,
                user_id: page.props.auth.user.id,
                thread_id: thread.value.id,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                type: 'user',
                is_read: false,
            };
            messages.value[tempId] = optimisticMsg;
            nextTick().then(() => {
                dialogElement.value.scrollTo({
                     top: dialogElement.value.scrollHeight,
                     behavior: 'smooth'
                 });
            });
            formMessage.reset();

            if (!route().current('admin.threads.*')) {
                axios.post(route('threads.store'), {
                    message: msgContent,
                    user_id: msgUserId,
                    offer_id: props.viewOffer ? props.viewOffer.id : undefined
                }).then(() => {
                    // Success
                }).catch(() => {
                    delete messages.value[tempId];
                    formMessage.message = msgContent;
                });
            } else {
                axios.post(route('admin.threads.store', thread.value), {
                    message: msgContent
                }).then(() => {
                    // Success
                }).catch(() => {
                    delete messages.value[tempId];
                    formMessage.message = msgContent;
                });
            }
        }
    }

}

const sendFile = () => {
    if (!checkAuth()) return;
    if (uploadedFile.value.file) {
        let form = new FormData();
        form.append('file', uploadedFile.value.file);

        if (!route().current('admin.threads.*')) {
            // Fix: User fallbacks similar to sendMsg
            let targetId = thread.value?.user?.id;
            if (!targetId && props.viewOffer?.user?.id) {
                targetId = props.viewOffer.user.id;
            }
            if (!targetId) {
                 return; // Silently fail or alert
            }
            form.append('user_id', targetId);
            
            if (props.viewOffer) {
                form.append('offer_id', props.viewOffer.id)
            }
            axios.post(route('threads.store'), form).then(() => {
                uploadedFile.value = {};
            });
        } else {
            axios.post(route('admin.threads.store', thread.value), form).then(() => {
                uploadedFile.value = {};
            });
        }

    }
}

const pasteHandler = (e) => {
    if (!checkAuth()) return false;
    const clipboardItems = e.clipboardData.items;

    // Fix: check if clipboardItems has items and safely access types to prevent crash
    if(!clipboardItems.length) return;

    // Check if the first item (usually the file) is an image or PDF
    const item = clipboardItems[0];
    const isImage = item.type.indexOf("image") !== -1;
    const isPdf = item.type === "application/pdf";

    if (!(isImage || isPdf)) return;

    // Proceed to handle the file
    const file = item.getAsFile();
    if (file) {
        uploadedFile.value.file = file;
        uploadedFile.value.url = URL.createObjectURL(file);
    }
}

const deleteUploadedFile = () => {
    uploadedFile.value = {};
}

defineExpose({
    dialogElement
})

const showImg = ($event) => {
    showModal.value = true
    dataImg.value = $event
}

const checkAuth = () => {
    if (!page.props.auth.user) {
        inputMsg.value?.querySelector('input, textarea')?.blur()
        needAuth.value = true
        return false
    }
    return true
}

const closeModal = () => {
    showModal.value = false
    dataImg.value = null
}

const handleGlobalKeydown = (e) => {
    if (e.key === 'Enter' && uploadedFile.value?.file && !e.shiftKey) {
        // If the user is focused on another input (e.g. search bar), don't send
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
            return;
        }
        e.preventDefault();
        sendFile();
    }
}

onMounted(() => {
    window.addEventListener('keydown', handleGlobalKeydown);
    dialogElement.value.addEventListener('scroll', () => {
        if (dialogElement.value.scrollTop < 50) {
            nextPageFunc()
        }
    })
    oldHeight.value = dialogElement.value.scrollHeight
    dialogElement.value.scrollBy(0, dialogElement.value.scrollHeight)
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleGlobalKeydown);
})

const isSystem = (message) => {
    return message.type !== 'user';
}

watch(() =>
        formMessage.message, (val) => {
        if(!thread.value) {
            inputs.value['untitled'] = formMessage.message;
        } else {
            inputs.value[thread.value.id] = formMessage.message;
        }
        if (val.length > 1) {
            checkAuth()
        }
    }
)

const getStyleStarReview = (rating) => {
    return rating <= 2 ? 'fill-red-500' : rating <= 3 ? 'fill-orange-500' : rating <= 4 ? 'fill-yellow-400' : 'fill-green-500'
}

const VueUploadComponent = shallowRef(null);
onMounted(async () => {
    if (typeof window !== 'undefined') {
        const module = await import("vue-upload-component");
        VueUploadComponent.value = module.default;
    }
});

</script>

<template>
    <div :class="contentClasses"
         class="rounded-lg bg-slate-200 dark:bg-slate-800 px-2.5 py-3 shadow-md flex flex-col gap-2.5 grow">
        <div class="flex justify-between items-center pr-1">
            <template v-if="!route().current('admin.threads.*')">
                <div class="flex gap-2.5">
                    <div class="flex gap-4 items-center pl-2">
                        <slot name="left"></slot>
                        <div
                            class="rounded-full border border-gray-400 shadow-md-1 min-h-[3.75rem] min-w-[3.75rem] overflow-hidden bg-slate-300 dark:bg-slate-700">
                            <template v-if="thread?.user">
                                <img class="w-[3.75rem] h-[3.75rem]" :src="thread.user.profile_photo_url" :alt="thread.user.name">
                            </template>
                        </div>
                    </div>

                    <div v-if="thread?.user">
                        <div class="flex gap-0.75 px-1 py-0.5 ">
                            <p class="tracking-wide dark:text-slate-200 break-all font-medium line-clamp-1">
                                {{ thread.user.name }}</p>
                            <Badge class="shrink-0 dark:text-dark-blue"
                                   :value="getUserStatusByIdOrStatus(thread.user.id, thread.user.is_online) ? 'Online' : 'Offline'"
                                   :type="getUserStatusByIdOrStatus(thread.user.id, thread.user.is_online) ? 'success': 'danger'"/>
                        </div>
                        <div class="text-xs dark:text-slate-200 tracking-wide font-light font-condensed px-1">
                            <span v-if="thread.user.reviews_count < 1">{{ $t('no reviews') }}</span>
                            <span v-else>{{ $t('reviews') }}: {{ thread.user.reviews_count }}</span>
                        </div>
                    </div>
                </div>
                <slot name="right"></slot>
            </template>
            <template v-else>
                <div class="w-full">
                    <div class="flex justify-between pb-1 lg:hidden">
                        <slot name="left"></slot>
                        <slot name="right"></slot>
                    </div>
                    <div class="grid grid-cols-2 gap-2 w-full">
                        <template v-for="participant in thread?.participants">
                            <div class="flex gap-2.5 flex-col lg:flex-row">
                                <div class="flex gap-4 items-center pl-2 mx-auto lg:m-0">
                                    <div
                                        class="rounded-full border border-gray-400 shadow-md-1 min-h-[3.75rem] min-w-[3.75rem] overflow-hidden bg-slate-300 dark:bg-slate-700">
                                        <img class="w-[3.75rem] h-[3.75rem]" :src="participant.user.profile_photo_url"
                                             :alt="participant.user.name">
                                    </div>
                                </div>
                                <div>
                                    <div class="flex gap-0.75 px-1 py-0.5 justify-center lg:justify-start">
                                        <p class="tracking-wide break-all font-medium line-clamp-1">{{
                                                participant.user.name
                                            }}</p>
                                        <Badge class="shrink-0 dark:text-dark-blue"
                                               :value="getUserStatusByIdOrStatus(participant.user.id, participant.user.is_online) ? 'Online' : 'Offline'"
                                               :type="getUserStatusByIdOrStatus(participant.user.id, participant.user.is_online) ? 'success': 'danger'"/>
                                    </div>
                                    <div
                                        class="px-1 pb-1 flex items-center border-b border-dashed border-slate-300 dark:border-slate-700 w-fit">
                                        <div class="flex items-center gap-1 leading-full">
                                            <StarReview class="w-[0.75rem] h-[0.75rem]"
                                                        :class="getStyleStarReview(participant.user.rating)"/>
                                            <div class="text-xs leading-full pt-0.5">{{ participant.user.rating }}</div>
                                        </div>
                                        <div class="py-0.5 px-1 text-xs leading-full text-slate-500">
                                            {{ participant.user.on_site_desc }}
                                        </div>
                                    </div>
                                    <div class="p-1 flex flex-col gap-1 text-xs leading-full font-medium">
                                        <div class="flex gap-2 text-green-500">
                                            <span>{{ $t('Purchases') }}: {{ participant.user.orders_count }}</span>
                                            <span>{{ participant.user.orders_amount_formatted }}</span>
                                        </div>
                                        <div class="flex gap-2 text-red-500">
                                            <span>{{ $t('Sales') }}: {{ participant.user.orders_count }}</span>
                                            <span>{{ participant.user.sells_amount_formatted }}</span></div>

                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

            </template>
        </div>
        <div v-if="route().current('threads.*') && thread?.user?.offer"
             class="px-2 py-1 text-sm tracking-wider leading-120 font-normal">
            <span class="text-slate-500 dark:text-slate-400 leading-120">{{ $t('Now watching') }}: </span>
            <Link :href="route('offers.show', thread?.user?.offer.id)">
                <span class="text-blue-600 leading-120 dark:text-cyan-300">{{ thread?.user?.offer.title }}</span>
            </Link>
        </div>
        <div ref="dialogElement"
             class="justify-end rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-dark-blue p-2.5  gap-4 tracking-wider overflow-y-auto relative flex-1 min-h-0">
            <div v-if="thread" class="overflow flex flex-col gap-4"
                 :class="sortedMessages.length < 3 ? 'min-h-[7.5rem] lg:min-h-[40vh]' : 'min-h-[40vh]'">
                <template v-if="sortedMessages.length === 0">
                    <div
                        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-500 text-sm tracking-wider font-normal whitespace-nowrap">
                        {{ $t('Send a message to the seller before purchasing') }}
                    </div>
                </template>
                <div v-if="isLoading && sortedMessages.length > 0" class="flex justify-center w-full py-2">
                    <Spinner class="w-6 h-6 text-gray-400 animate-spin fill-blue-600 dark:fill-cyan-300"/>
                </div>
                <template v-for="(message, index) in sortedMessages" :key="message.id">
                    <message-date
                        v-if="dateComparison(sortedMessages[index - 1]?.created_at, message?.created_at)"
                        :date="dayjs(message?.created_at).format('D MMMM')"/>
                    <MessageSystem :participants="thread.participants" v-if="isSystem(message)"
                                   :message="message"/>
                    <MessageString @showImg="showImg($event)" v-else
                                   :message="message"></MessageString>

                </template>
            </div>

            <template v-else>
                <AkLogo class="w-[220px] h-[256px] logo-gray dark:!fill-red-500 my-12 mx-auto"></AkLogo>
                <div v-if="haveChat"
                     class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-base tracking-wider dark:text-slate-200 font-medium whitespace-nowrap">
                    {{ $t('Select a chat in the left column') }}
                </div>
            </template>
        </div>
        <div class="flex pl-2.5 gap-2.5 items-center shrink-0" ref="inputMsg">
            <component :is="VueUploadComponent"
                       v-if="VueUploadComponent"
                class="!flex items-center cursor-pointer h-fit hover:bg-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                extensions="jpg,jpeg,png,pdf"
                accept="image/png,image/jpg,image/jpeg,application/pdf"
                :multiple="false"
                :size="1024 * 1024 * 10"
                v-model="files"
                @input-filter="inputFilter"
                :disabled="!thread"
                ref="upload">
                <img src="/assets/icons/clip.svg" alt="" class="shrink-0"/>
            </component>

            <div class="flex flex-col w-full">
                <Input v-if="!uploadedFile?.file" v-model="formMessage.message"
                       :placeholder="$t('Enter message...')"
                       @onFocus="checkAuth"
                       @paste="pasteHandler"
                       @push="sendMsg"
                       text-area
                >
                    <template #right-icon>
                        <button @click="sendMsg">
                            <MaskIcon src="/assets/icons/send.svg"
                                  :class="{'!bg-blue-600 dark:!bg-cyan-300': formMessage.message?.length > 0}"
                                  class="w-6 h-6 bg-slate-300 dark:bg-slate-700"/>
                        </button>
                    </template>
                </Input>
                <div v-if="uploadedFile?.file" class="border border-slate-300 dark:border-slate-700 rounded-lg bg-white justify-between dark:bg-dark-blue flex items-center p-4 w-full gap-3 transition-colors">
                    <div class="w-20 h-20 rounded-lg flex items-center justify-center border border-slate-300 relative">
                        <button class="absolute right-1 top-1" @click="deleteUploadedFile">
                            <ion-icon name="add-circle" class="w-6 fill-red-500 bg-white rounded-full rotate-45 dark:stroke-blue-500" style="width: 24px; height: 24px"></ion-icon>
                        </button>
                        <p class="text-sm w-full font-sans truncate" v-if="uploadedFile?.file.type === 'application/pdf'">
                            {{uploadedFile?.file?.name}}
                        </p>
                        <img v-if="uploadedFile?.file.type .substr(0, 6) === 'image/'" :src='uploadedFile.url' class="w-full" :alt="uploadedFile.file.name">
                    </div>
                    <button @click="sendFile">
                        <MaskIcon src="/assets/icons/send.svg" class="w-6 h-6 bg-blue-600 dark:!bg-cyan-300 dark:bg-slate-700"/>
                    </button>
                </div>
            </div>


        </div>
        <Loading v-if="loading"></Loading>
    </div>

    <Modal :show="showModal" @close="closeModal()">
        <img v-if="!dataImg.includes('.pdf')" class="m-auto" :src="dataImg" :alt="$t('Picture')">
        <div class="h-[80vh] lg:h-[60vh]" v-if="dataImg.includes('.pdf')">
            <embed class="w-full h-full" :src="dataImg" type="">
        </div>
    </Modal>

    <Modal :show="needAuth" max-width="300" @close="needAuth = false">
        <div class="dark:text-slate-200 py-5 px-3 flex flex-col gap-3">
            <div class="px-2.5 text-sm font-normal leading-120">
                {{ $t('You must be logged in to send chat messages.') }}
            </div>
            <div class="flex m-auto w-full px-2">
                <Button :text="$t('Sign in')" type="primary" @click="router.get(route('login'))"
                        class="text-sm w-full"/>
            </div>
        </div>
    </Modal>

</template>

<style scoped>

</style>
